import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { Box, Grid, Typography, useMediaQuery } from "@mui/material";
import { AccessTime as AccessTimeIcon, PlayCircleOutline as PlayCircleOutlineIcon, HourglassEmpty as HourglassEmptyIcon, Devices as DevicesIcon } from "@mui/icons-material";
import dayjs from "dayjs";

import { UpdateStatCompBox } from "../../ReusableComponents/CustomDesignMUI/CustomMUI";
import EmployeeProgress from "./EmployeeProgress";
import { formatTime } from "../../ReusableComponents/UtilityFunctions";
import { BreakTimeBox, CheckInBox, CheckOutBox, MainGrid, WorkingHrsBox } from "../../../Styles/UpdateEmployee/StatisticsCompStyle";

const StatComp = ({ EmpId, calendarValue, averageMode = "Monthly" }) => {
	const { employeeId } = useParams();
	const isMobile = useMediaQuery("(max-width:600px)");
	const allEmployeeAttendence = useSelector(
		(state) => state.employeeData.allEmployeeAttendenceData
	);
	const selectedEmpId = employeeId || EmpId;

	const [attendenceData, setAttendenceData] = useState([
		{
			averageBreakTime: 0,
			averageInTime: 0,
			averageOutTime: 0,
			averageTotalTime: 0,
		},
	]);

	const [monthlyStats, setMonthlyStats] = useState({
		averageBreakTime: 0,
		averageInTime: "00:00:00",
		averageOutTime: "00:00:00",
		averageTotalTime: 0,
		presentDays: 0,
	});

	// Function to calculate monthly statistics for selected date
	const calculateMonthlyStats = (date) => {
		const employeeData = attendenceData[0];
		if (!employeeData?.records) return;

		const selectedMonth = date.month();
		const selectedYear = date.year();

		// Filter records for the selected month
		const monthRecords = employeeData.records.filter((record) => {
			const recordDate = dayjs(record.date, "DD-MM-YYYY");
			return recordDate.month() === selectedMonth && recordDate.year() === selectedYear;
		});

		if (monthRecords.length === 0) {
			setMonthlyStats({
				averageBreakTime: 0,
				averageInTime: "00:00:00",
				averageOutTime: "00:00:00",
				averageTotalTime: 0,
				presentDays: 0,
			});
			return;
		}

		// Calculate averages for the month
		let totalBreakTime = 0;
		let totalInTimeSeconds = 0;
		let totalOutTimeSeconds = 0;
		let totalWorkingTime = 0;
		let validInTimes = 0;
		let validOutTimes = 0;

		monthRecords.forEach((record) => {
			if (record.dailytimer?.BreakTime) {
				totalBreakTime += record.dailytimer.BreakTime;
			}
			if (record.dailytimer?.TimerStart) {
				const inTime = dayjs(record.dailytimer.TimerStart);
				totalInTimeSeconds += inTime.hour() * 3600 + inTime.minute() * 60 + inTime.second();
				validInTimes++;
			}
			if (record.dailytimer?.TimerEnd) {
				const outTime = dayjs(record.dailytimer.TimerEnd);
				totalOutTimeSeconds += outTime.hour() * 3600 + outTime.minute() * 60 + outTime.second();
				validOutTimes++;
			}
			if (record.dailytimer?.TotalTime) {
				totalWorkingTime += record.dailytimer.TotalTime;
			}
		});

		// Calculate averages
		const avgBreakTime = totalBreakTime / monthRecords.length;
		const avgInTime = validInTimes > 0 ? totalInTimeSeconds / validInTimes : 0;
		const avgOutTime = validOutTimes > 0 ? totalOutTimeSeconds / validOutTimes : 0;
		const avgTotalTime = totalWorkingTime / monthRecords.length;

		// Convert seconds to HH:MM:SS format
		const secondsToTime = (seconds) => {
			const hours = Math.floor(seconds / 3600);
			const minutes = Math.floor((seconds % 3600) / 60);
			const secs = Math.floor(seconds % 60);
			return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
		};

		setMonthlyStats({
			averageBreakTime: avgBreakTime,
			averageInTime: secondsToTime(avgInTime),
			averageOutTime: secondsToTime(avgOutTime),
			averageTotalTime: avgTotalTime,
			presentDays: monthRecords.length,
		});
	};

	// Update monthly stats when date or attendance data changes
	useEffect(() => {
		if (attendenceData[0]?.records && calendarValue) {
			calculateMonthlyStats(dayjs(calendarValue));
		}
	}, [calendarValue, attendenceData]);

	function convertTo12HourFormat(time) {
		const [hours, minutes, seconds] = time?.split(":").map(Number);
		if (time === "00:00:00") {
			return `00:00:00 Hrs`;
		}
		let formattedHours = hours === "00" ? hours : hours % 12 || 12;
		const ampm = hours < 12 ? "AM" : "PM";

		const formattedMinutes = String(minutes).padStart(2, "0");
		const formattedSeconds = String(seconds).padStart(2, "0");

		return `${formattedHours}:${formattedMinutes}:${formattedSeconds} ${ampm}`;
	}

	useEffect(() => {
		const filteredDataByEmployee = allEmployeeAttendence?.filter(
			(item) => item?.empId === selectedEmpId
		);
		if (filteredDataByEmployee?.length !== 0) {
			setAttendenceData(filteredDataByEmployee);
		}
	}, [selectedEmpId, allEmployeeAttendence]);

	return (
		<Box>
			<Grid container spacing={{ xs: 2, md: 2 }}>
				<Grid item xs={12} md={6}>
					<Box>
						<EmployeeProgress EmpId={EmpId} />
					</Box>
				</Grid>
				<MainGrid
					ismobile={isMobile ? "true" : "false"}
					item
					xs={12}
					md={6}
					style={{marginTop: 0}}
				>
					<CheckInBox updatestatcompbox={UpdateStatCompBox}>
						<AccessTimeIcon sx={{ color: "var(--secondary-color)" }} />
						<Box sx={{ marginLeft: "10px" }}>
							<Typography sx={{ fontWeight: "bold", fontSize: "13px" }}>
								Average CheckIn Time
							</Typography>
							<Typography sx={{ fontSize: "13px" }}>
								{convertTo12HourFormat(
									averageMode === "Monthly"
										? monthlyStats.averageInTime || "00:00:00"
										: attendenceData[0]?.averageInTime || "00:00:00"
								)}
							</Typography>
						</Box>
					</CheckInBox>
					<CheckOutBox updatestatcompbox={UpdateStatCompBox}>
						<PlayCircleOutlineIcon
							sx={{ color: "var(--reject-color)" }}
						/>
						<Box sx={{ marginLeft: "10px" }}>
							<Typography sx={{ fontWeight: "bold", fontSize: "13px" }}>
								Average CheckOut Time
							</Typography>
							<Typography sx={{ fontSize: "13px" }}>
								{convertTo12HourFormat(
									averageMode === "Monthly"
										? monthlyStats.averageOutTime || "00:00:00"
										: attendenceData[0]?.averageOutTime || "00:00:00"
								)}
							</Typography>
						</Box>
					</CheckOutBox>
					<BreakTimeBox updatestatcompbox={UpdateStatCompBox}>
						<HourglassEmptyIcon
							sx={{ color: "var(--totalHrs-color)" }}
						/>
						<Box sx={{ marginLeft: "10px" }}>
							<Typography
								sx={{ fontWeight: "bold", fontSize: "13px" }}
							>
								Average Break Time
							</Typography>
							<Typography sx={{ fontSize: "13px" }}>
								{averageMode === "Monthly"
									? formatTime(Math.floor((monthlyStats.averageBreakTime || 0) / 1000)) || 0
									: formatTime(Math.floor((attendenceData[0]?.averageBreakTime || 0) / 1000)) || 0
								} Hrs
							</Typography>
						</Box>
					</BreakTimeBox>
					<WorkingHrsBox updatestatcompbox={UpdateStatCompBox}>
						<DevicesIcon
							sx={{ color: "var(--avgWorkingHrs-color)" }}
						/>
						<Box sx={{ marginLeft: "10px" }}>
							<Typography
								sx={{ fontWeight: "bold", fontSize: "13px" }}
							>
								Average Working hrs
							</Typography>
							<Typography sx={{ fontSize: "13px" }}>
								{averageMode === "Monthly"
									? formatTime(Math.floor((monthlyStats.averageTotalTime || 0) / 1000))
									: formatTime(Math.floor((attendenceData[0]?.averageTotalTime || 0) / 1000))
								} Hrs
							</Typography>
						</Box>
					</WorkingHrsBox>
				</MainGrid>
			</Grid>
		</Box>
	);
};

export default StatComp;
